import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/EcommerceDashboardAdvanced.css';

interface AdvancedAnalytics {
  revenue: {
    current: number;
    previous: number;
    growth: number;
  };
  orders: {
    current: number;
    previous: number;
    growth: number;
  };
  customers: {
    current: number;
    previous: number;
    growth: number;
  };
  conversion: {
    rate: number;
    previous: number;
    growth: number;
  };
  topProducts: Array<{
    name: string;
    revenue: number;
    units: number;
    growth: number;
  }>;
  salesByCategory: Array<{
    category: string;
    amount: number;
    percentage: number;
  }>;
  recentOrders: Array<{
    id: string;
    customer: string;
    amount: number;
    status: 'completed' | 'pending' | 'processing';
    date: string;
  }>;
  monthlyTrends: Array<{
    month: string;
    revenue: number;
    orders: number;
  }>;
  inventory: {
    lowStock: number;
    outOfStock: number;
    totalProducts: number;
  };
}

const EcommerceDashboardAdvanced: React.FC = () => {
  const [analytics, setAnalytics] = useState<AdvancedAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedView, setSelectedView] = useState<'overview' | 'products' | 'customers' | 'inventory'>('overview');
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Simulate API call with realistic e-commerce data
    setTimeout(() => {
      setAnalytics({
        revenue: { current: 847230, previous: 723500, growth: 17.1 },
        orders: { current: 3247, previous: 2891, growth: 12.3 },
        customers: { current: 1849, previous: 1623, growth: 13.9 },
        conversion: { rate: 3.84, previous: 3.21, growth: 19.6 },
        topProducts: [
          { name: 'Wireless Bluetooth Headphones', revenue: 89450, units: 342, growth: 23.5 },
          { name: 'Smart Fitness Tracker', revenue: 67890, units: 287, growth: -4.2 },
          { name: 'Portable Charger 20000mAh', revenue: 45678, units: 198, growth: 45.8 },
          { name: 'Wireless Phone Charger', revenue: 34567, units: 156, growth: 12.1 },
          { name: 'USB-C Hub Adapter', revenue: 28934, units: 134, growth: 8.7 }
        ],
        salesByCategory: [
          { category: 'Electronics', amount: 342560, percentage: 40.4 },
          { category: 'Fashion', amount: 234890, percentage: 27.7 },
          { category: 'Home & Garden', amount: 156780, percentage: 18.5 },
          { category: 'Sports', amount: 87450, percentage: 10.3 },
          { category: 'Books', amount: 25550, percentage: 3.0 }
        ],
        recentOrders: [
          { id: '#ORD-2024-001', customer: 'Sarah Chen', amount: 234.99, status: 'completed', date: '2 min ago' },
          { id: '#ORD-2024-002', customer: 'Michael Johnson', amount: 89.50, status: 'processing', date: '5 min ago' },
          { id: '#ORD-2024-003', customer: 'Emily Rodriguez', amount: 156.75, status: 'pending', date: '8 min ago' },
          { id: '#ORD-2024-004', customer: 'David Kim', amount: 67.25, status: 'completed', date: '12 min ago' },
          { id: '#ORD-2024-005', customer: 'Anna Thompson', amount: 298.00, status: 'processing', date: '15 min ago' }
        ],
        monthlyTrends: [
          { month: 'Jan', revenue: 567890, orders: 2450 },
          { month: 'Feb', revenue: 623450, orders: 2678 },
          { month: 'Mar', revenue: 598760, orders: 2534 },
          { month: 'Apr', revenue: 689340, orders: 2891 },
          { month: 'May', revenue: 734560, orders: 3102 },
          { month: 'Jun', revenue: 847230, orders: 3247 }
        ],
        inventory: {
          lowStock: 23,
          outOfStock: 7,
          totalProducts: 1847
        }
      });
      setLoading(false);
    }, 1500);
  }, [selectedTimeRange]);

  // Real-time updates simulation
  useEffect(() => {
    if (!realTimeUpdates || !analytics) return;
    
    const interval = setInterval(() => {
      setAnalytics(prev => {
        if (!prev) return prev;
        
        // Simulate small random changes in real-time data
        const revenueChange = Math.random() * 1000 - 500;
        const ordersChange = Math.random() > 0.7 ? (Math.random() > 0.5 ? 1 : -1) : 0;
        
        return {
          ...prev,
          revenue: {
            ...prev.revenue,
            current: Math.max(0, prev.revenue.current + revenueChange)
          },
          orders: {
            ...prev.orders,
            current: Math.max(0, prev.orders.current + ordersChange)
          }
        };
      });
    }, 3000);
    
    return () => clearInterval(interval);
  }, [realTimeUpdates, analytics]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'processing': return '#F59E0B';
      case 'pending': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatGrowth = (growth: number) => {
    const sign = growth >= 0 ? '+' : '';
    return `${sign}${growth.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="ecommerce-loading">
        <div className="loading-spinner"></div>
        <p>Loading analytics dashboard...</p>
      </div>
    );
  }

  return (
    <div className="ecommerce-dashboard-advanced">
      <div className="ecommerce-header">
        <div className="header-left">
          <Link to="/" className="back-button-ecommerce">← Back to Home</Link>
          <div className="title-section">
            <h1>📈 E-commerce Command Center</h1>
            <div className="version-badge-ecommerce v15">Synoppy 1.5</div>
          </div>
        </div>
        
        <div className="header-controls">
          <div className="time-range-selector">
            {(['7d', '30d', '90d', '1y'] as const).map(range => (
              <button
                key={range}
                className={selectedTimeRange === range ? 'active' : ''}
                onClick={() => setSelectedTimeRange(range)}
              >
                {range}
              </button>
            ))}
          </div>
          
          <div className="real-time-toggle">
            <label>
              <input
                type="checkbox"
                checked={realTimeUpdates}
                onChange={(e) => setRealTimeUpdates(e.target.checked)}
              />
              <span>Real-time Updates</span>
              {realTimeUpdates && <div className="pulse-indicator"></div>}
            </label>
          </div>
        </div>
      </div>

      <div className="view-tabs">
        {(['overview', 'products', 'customers', 'inventory'] as const).map(view => (
          <button
            key={view}
            className={selectedView === view ? 'active' : ''}
            onClick={() => setSelectedView(view)}
          >
            {view.charAt(0).toUpperCase() + view.slice(1)}
          </button>
        ))}
      </div>

      {analytics && (
        <>
          {selectedView === 'overview' && (
            <div className="overview-section">
              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="kpi-header">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-trend positive">
                      {formatGrowth(analytics.revenue.growth)}
                    </div>
                  </div>
                  <div className="kpi-value">{formatCurrency(analytics.revenue.current)}</div>
                  <div className="kpi-label">Total Revenue</div>
                  <div className="kpi-comparison">
                    vs {formatCurrency(analytics.revenue.previous)} last period
                  </div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-header">
                    <div className="kpi-icon">🛒</div>
                    <div className="kpi-trend positive">
                      {formatGrowth(analytics.orders.growth)}
                    </div>
                  </div>
                  <div className="kpi-value">{analytics.orders.current.toLocaleString()}</div>
                  <div className="kpi-label">Total Orders</div>
                  <div className="kpi-comparison">
                    vs {analytics.orders.previous.toLocaleString()} last period
                  </div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-header">
                    <div className="kpi-icon">👥</div>
                    <div className="kpi-trend positive">
                      {formatGrowth(analytics.customers.growth)}
                    </div>
                  </div>
                  <div className="kpi-value">{analytics.customers.current.toLocaleString()}</div>
                  <div className="kpi-label">Active Customers</div>
                  <div className="kpi-comparison">
                    vs {analytics.customers.previous.toLocaleString()} last period
                  </div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-header">
                    <div className="kpi-icon">🎯</div>
                    <div className="kpi-trend positive">
                      {formatGrowth(analytics.conversion.growth)}
                    </div>
                  </div>
                  <div className="kpi-value">{analytics.conversion.rate}%</div>
                  <div className="kpi-label">Conversion Rate</div>
                  <div className="kpi-comparison">
                    vs {analytics.conversion.previous}% last period
                  </div>
                </div>
              </div>

              <div className="charts-row">
                <div className="chart-container">
                  <h3>Revenue Trend</h3>
                  <div className="revenue-chart">
                    {analytics.monthlyTrends.map((month, index) => (
                      <div key={index} className="chart-column">
                        <div 
                          className="chart-bar"
                          style={{ 
                            height: `${(month.revenue / Math.max(...analytics.monthlyTrends.map(m => m.revenue))) * 100}%` 
                          }}
                          title={`${month.month}: ${formatCurrency(month.revenue)}`}
                        ></div>
                        <div className="chart-label">{month.month}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="chart-container">
                  <h3>Sales by Category</h3>
                  <div className="category-chart">
                    {analytics.salesByCategory.map((cat, index) => (
                      <div key={index} className="category-item">
                        <div className="category-info">
                          <span className="category-name">{cat.category}</span>
                          <span className="category-amount">{formatCurrency(cat.amount)}</span>
                        </div>
                        <div className="category-bar">
                          <div 
                            className="category-fill"
                            style={{ width: `${cat.percentage}%` }}
                          ></div>
                        </div>
                        <span className="category-percent">{cat.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="recent-orders-section">
                <h3>Recent Orders</h3>
                <div className="orders-table">
                  <div className="orders-header">
                    <div>Order ID</div>
                    <div>Customer</div>
                    <div>Amount</div>
                    <div>Status</div>
                    <div>Time</div>
                  </div>
                  {analytics.recentOrders.map((order, index) => (
                    <div key={index} className="order-row">
                      <div className="order-id">{order.id}</div>
                      <div className="customer-name">{order.customer}</div>
                      <div className="order-amount">{formatCurrency(order.amount)}</div>
                      <div className="order-status">
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(order.status) }}
                        >
                          {order.status}
                        </span>
                      </div>
                      <div className="order-time">{order.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedView === 'products' && (
            <div className="products-section">
              <div className="top-products">
                <h3>Top Performing Products</h3>
                <div className="products-list">
                  {analytics.topProducts.map((product, index) => (
                    <div key={index} className="product-card">
                      <div className="product-rank">#{index + 1}</div>
                      <div className="product-details">
                        <div className="product-name">{product.name}</div>
                        <div className="product-metrics">
                          <span>Revenue: {formatCurrency(product.revenue)}</span>
                          <span>Units: {product.units}</span>
                        </div>
                      </div>
                      <div className={`product-growth ${product.growth >= 0 ? 'positive' : 'negative'}`}>
                        {formatGrowth(product.growth)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedView === 'inventory' && (
            <div className="inventory-section">
              <div className="inventory-overview">
                <div className="inventory-card">
                  <div className="inventory-icon">📦</div>
                  <div className="inventory-value">{analytics.inventory.totalProducts}</div>
                  <div className="inventory-label">Total Products</div>
                </div>
                <div className="inventory-card warning">
                  <div className="inventory-icon">⚠️</div>
                  <div className="inventory-value">{analytics.inventory.lowStock}</div>
                  <div className="inventory-label">Low Stock Items</div>
                </div>
                <div className="inventory-card danger">
                  <div className="inventory-icon">🚫</div>
                  <div className="inventory-value">{analytics.inventory.outOfStock}</div>
                  <div className="inventory-label">Out of Stock</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="version-comparison-ecommerce">
        <Link to="?version=1.0" className="downgrade-link-ecommerce">
          Compare with Synoppy 1.0 Version →
        </Link>
      </div>

      <div className="features-showcase">
        <div className="feature">📊 Real-time analytics</div>
        <div className="feature">🔄 Live data synchronization</div>
        <div className="feature">📈 Advanced visualization</div>
        <div className="feature">🎯 Customer segmentation</div>
        <div className="feature">🤖 Predictive insights</div>
        <div className="feature">📱 Mobile responsive</div>
      </div>
    </div>
  );
};

export default EcommerceDashboardAdvanced;